import { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import { useVisitsKanban } from '@pages/authenticated/agency-pages/visits/useVisitsKanban';
import type { KanbanVisit } from '@pages/authenticated/agency-pages/visits/useVisitsKanban';
import { useAgency } from '@pages/authenticated/agency-pages/AgenciesContext';
import { useAgencyPermissions } from '@/features/agency';
import { useLayout } from '@layout/app/LayoutContext';
import { useAuth } from '@context/authentication';
import { DEFAULT_VISIT_PERIOD, type VisitFilters } from '../components/VisitFilterPanel';
import { useVisitStatusLabels } from '../constants/visit-colors';
import type { VisitStatus } from '../constants/visit-colors';
import { getPeriodDates, getInitialMobileKanbanLayout, persistMobileKanbanLayout, type ViewMode, type MobileKanbanLayout } from './ManageVisitsPage.helpers';
import { buildVisitTableColumns, buildPeriodOptions } from './ManageVisitsPage.columns';
import { useUrlFilters } from '@/shared/hooks/use-url-filters';
import { useIsSoloBroker } from '@/shared/hooks/use-is-solo-broker';
import {
  useVisitFiltering,
  createStatusChangeHandler,
  createDeleteVisitHandler,
  createRemoveFilterHandler,
} from './ManageVisitsPage.handlers';
import {
  useFeedbackCancelDialogs,
  buildActiveFilterLabels,
  createNewVisitHandler,
  countActiveFilters,
  createConfirmFeedbackHandler,
  createConfirmCancelHandler,
} from './ManageVisitsPage.useManageVisits.helpers';

/**
 * Reads the URL-backed visit filter state and exposes typed getters and setters
 * for period, search query, and status/broker filters.
 *
 * @returns The period, search, and filter values with their setters.
 */
function useVisitUrlFilters() {
  const FILTER_DEFAULTS = useMemo(
    () => ({ q: '', period: DEFAULT_VISIT_PERIOD, statuses: [] as string[], brokers: [] as string[] }),
    [],
  );
  const { filters: urlFilters, setFilters: setUrlFilters } = useUrlFilters(FILTER_DEFAULTS);
  const filters: VisitFilters = useMemo(() => ({
    statuses: (urlFilters.statuses as string[])?.length ? urlFilters.statuses as VisitStatus[] : undefined,
    brokers: (urlFilters.brokers as string[])?.length ? urlFilters.brokers as string[] : undefined,
    period: urlFilters.period as string,
  }), [urlFilters.statuses, urlFilters.brokers, urlFilters.period]);
  return {
    period: urlFilters.period as string,
    search: urlFilters.q as string,
    setSearch: (v: string) => setUrlFilters({ q: v }),
    filters,
    /*
     * Period travels with the other filters instead of on its own setter:
     * `useUrlFilters` keeps one call per tick, so applying the panel with
     * two setters dropped whichever wrote first.
     */
    setFilters: (f: VisitFilters) =>
      setUrlFilters({
        statuses: f.statuses ?? [],
        brokers: f.brokers ?? [],
        period: f.period ?? DEFAULT_VISIT_PERIOD,
      }),
  };
}

/**
 * Opens the new-visit drawer when the URL carries `?action=new`, then strips
 * the parameter from the URL.
 *
 * @param setDrawerOpen - Setter to open the visit drawer.
 */
function useOpenDrawerFromAction(setDrawerOpen: (v: boolean) => void) {
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get('action') !== 'new') return;
    setDrawerOpen(true);
    const next = new URLSearchParams(searchParams);
    next.delete('action');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, setDrawerOpen]);
}

/**
 * Syncs the opened visit detail overlay with the `?visit=<id>` query param so
 * the open state is reproducible on reload or when the URL is shared.
 *
 * @returns detailVisitId, openDetail, closeDetail.
 */
function useDetailVisitParam() {
  const [searchParams, setSearchParams] = useSearchParams();
  const detailVisitId = searchParams.get('visit') ?? null;

  const openDetail = useCallback(
    (id: string) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('visit', id);
        return next;
      }, { replace: true });
    },
    [setSearchParams],
  );

  const closeDetail = useCallback(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('visit');
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  return { detailVisitId, openDetail, closeDetail };
}

/**
 * State, derived data, and handlers for the visits management page. Extracted
 * from {@link ManageVisitsPage} so the page stays a thin orchestrator.
 */
export function useManageVisits() {
  const { t } = useTranslation('@pages/agencies');
  const statusLabels = useVisitStatusLabels();

  const [viewMode, setViewMode] = useState<ViewMode>('kanban');
  const [mobileKanbanLayout, setMobileKanbanLayoutState] = useState<MobileKanbanLayout>(getInitialMobileKanbanLayout);
  const setMobileKanbanLayout = (layout: MobileKanbanLayout) => {
    setMobileKanbanLayoutState(layout);
    persistMobileKanbanLayout(layout);
  };
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const { period, search, setSearch, filters, setFilters } = useVisitUrlFilters();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingVisit, setEditingVisit] = useState<KanbanVisit | null>(null);
  const dialogs = useFeedbackCancelDialogs();

  const { startDate, endDate } = getPeriodDates(period);
  const { visits, isLoading, refetch, updateStatus } = useVisitsKanban(startDate, endDate);
  const { agency } = useAgency((state) => state);
  const isSoloBroker = useIsSoloBroker();
  const { canAddVisit, currentUserRole } = useAgencyPermissions();
  const { updatePopupProps } = useLayout((state) => state);
  const { user } = useAuth();

  useOpenDrawerFromAction(setDrawerOpen);

  const { detailVisitId, openDetail, closeDetail } = useDetailVisitParam();

  const filteredVisits = useVisitFiltering(visits, search, filters);
  const periodOptions = buildPeriodOptions(t);
  const filterCount = countActiveFilters(filters);
  const activeFilterLabels = buildActiveFilterLabels(filters, statusLabels, agency, periodOptions);

  const handleNewVisit = createNewVisitHandler(
    canAddVisit, currentUserRole, setEditingVisit, setDrawerOpen, t,
  );

  const handleStatusChange = createStatusChangeHandler({
    visits, updateStatus,
    setFeedbackVisitId: dialogs.setFeedbackVisitId,
    setFeedbackRating: dialogs.setFeedbackRating,
    setFeedbackObs: dialogs.setFeedbackObs,
    setFeedbackDialogOpen: dialogs.setFeedbackDialogOpen,
    setCancelVisitId: dialogs.setCancelVisitId,
    setCancelReason: dialogs.setCancelReason,
    setCancelDialogOpen: dialogs.setCancelDialogOpen,
    t,
  });

  const handleConfirmFeedback = createConfirmFeedbackHandler(updateStatus, dialogs);
  const handleConfirmCancel = createConfirmCancelHandler(updateStatus, dialogs);

  const handleDeleteVisit = createDeleteVisitHandler(
    agency.id ?? '', setSelectedId, refetch, updatePopupProps, t,
  );
  const handleRemoveFilter = createRemoveFilterHandler(statusLabels, filters, setFilters, agency, periodOptions);
  const handleClearAll = () => setFilters({ period: DEFAULT_VISIT_PERIOD });

  return {
    t, viewMode, setViewMode, mobileKanbanLayout, setMobileKanbanLayout, period, setSearch, selectedId, setSelectedId,
    filtersOpen, setFiltersOpen, filters, setFilters, drawerOpen, setDrawerOpen,
    editingVisit, setEditingVisit,
    detailVisitId, openDetail, closeDetail,
    feedbackDialogOpen: dialogs.feedbackDialogOpen, setFeedbackDialogOpen: dialogs.setFeedbackDialogOpen,
    feedbackRating: dialogs.feedbackRating, setFeedbackRating: dialogs.setFeedbackRating,
    feedbackObs: dialogs.feedbackObs, setFeedbackObs: dialogs.setFeedbackObs,
    cancelDialogOpen: dialogs.cancelDialogOpen, setCancelDialogOpen: dialogs.setCancelDialogOpen,
    cancelReason: dialogs.cancelReason, setCancelReason: dialogs.setCancelReason,
    isLoading, refetch, agency, user, filteredVisits, filterCount, activeFilterLabels,
    handleNewVisit, handleStatusChange, handleConfirmFeedback, handleConfirmCancel,
    handleDeleteVisit, handleRemoveFilter, handleClearAll,
    isSoloBroker,
    tableColumns: buildVisitTableColumns(t, { showBroker: !isSoloBroker }),
    periodOptions,
  };
}
